(() => {
  if (window.__fifthCollectiveCartDrawerExperienceLoaded) return;
  window.__fifthCollectiveCartDrawerExperienceLoaded = true;

  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  const state = {
    lastAddButton: null,
    pendingVariantId: null,
    keysBeforeAdd: new Set(),
    flight: null,
    blockDrawerUntil: 0,
    emptyAnimationRun: false,
    drawerWasActive: false,
    addStartedAt: -Infinity,
    suppressAutoOpen: false,
    manualOpenUntil: -Infinity
  };

  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const mix = (a, b, t) => a + (b - a) * t;
  const smoother = (t) => {
    t = clamp(t);
    return t * t * t * (t * (t * 6 - 15) + 10);
  };


  const NAIL_BUBBLES = [
    [131, 96, 6.6], [118, 83, 8.3], [101, 76, 10.4], [82, 79, 10.2], [67, 92, 9.0],
    [56, 111, 10.9], [52, 134, 12.0], [55, 158, 11.4], [65, 178, 9.3], [81, 194, 10.0],
    [101, 201, 10.5], [119, 197, 8.3], [132, 186, 6.6], [111, 96, 4.0], [95, 91, 4.8],
    [79, 94, 4.7], [67, 106, 4.2], [60, 122, 5.1], [59, 139, 5.8], [60, 156, 5.2],
    [67, 172, 4.2], [79, 184, 4.7], [95, 188, 4.8], [111, 183, 4.0], [129, 116, 2.9],
    [130, 162, 2.9], [126, 141, 2.2]
  ];

  const traceNailShape = (ctx) => {
    ctx.beginPath();
    ctx.moveTo(100, 10);
    ctx.bezierCurveTo(124, 17, 152, 60, 162, 133);
    ctx.bezierCurveTo(173, 218, 167, 285, 143, 315);
    ctx.bezierCurveTo(124, 338, 76, 338, 57, 315);
    ctx.bezierCurveTo(33, 285, 27, 218, 38, 133);
    ctx.bezierCurveTo(48, 60, 76, 17, 100, 10);
    ctx.closePath();
  };

  const drawNailArt = (ctx, width, height) => {
    ctx.save();
    ctx.scale(width / 200, height / 340);
    ctx.translate(-100, -170);

    const base = ctx.createLinearGradient(100, 8, 100, 332);
    base.addColorStop(0, '#1E1E1E');
    base.addColorStop(.34, '#0B0B0B');
    base.addColorStop(1, '#020202');
    traceNailShape(ctx);
    ctx.fillStyle = base;
    ctx.fill();

    ctx.save();
    traceNailShape(ctx);
    ctx.clip();
    ctx.fillStyle = '#FFFFFF';
    for (const [x, y, radius] of NAIL_BUBBLES) {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    ctx.restore();
  };

  const cubic = (p0, p1, p2, p3, t) => {
    const u = 1 - t;
    const uu = u * u;
    const tt = t * t;
    return {
      x: uu * u * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + tt * t * p3.x,
      y: uu * u * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + tt * t * p3.y
    };
  };

  const getDrawer = () => document.querySelector('cart-drawer');
  const getRows = () => Array.from(document.querySelectorAll('#CartDrawer-CartItems [data-cc-cart-key]'));
  const currentKeys = () => new Set(getRows().map((row) => row.dataset.ccCartKey).filter(Boolean));

  /* ---------- Cart count: one source of truth ----------
     The badge's number is only ever *read* from a bubble Shopify has just
     rendered (or from a cart response) — never from our own writes to it. Each
     server-rendered bubble is stamped the moment it is read, so any later change
     to that same bubble is known to be ours and is ignored as data.
       shown   — the number the badge displays now
       pending — Shopify's newer number, held until the flying stone lands
       freshAt — when the last authoritative number arrived */
  const readCount = (root = document.getElementById('cart-icon-bubble')) => {
    const badge = root && root.querySelector('.cart-count-bubble');
    if (!badge) return 0;
    const node = badge.querySelector('span[aria-hidden="true"]');
    /* The Liquid leaves the figure out from 100 items up. */
    return node ? (parseInt(node.textContent.trim(), 10) || 0) : 100;
  };

  const renderStamp = (bubble) => bubble.querySelector('[data-cc-cart-motion]') || bubble.firstElementChild || bubble;

  /* Returns Shopify's count if the bubble is a render we have not read yet,
     otherwise null (the change was our own). */
  const takeFreshRender = () => {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return null;
    const stamp = renderStamp(bubble);
    if (stamp.dataset.ccCountRead === 'true') return null;
    stamp.dataset.ccCountRead = 'true';
    return readCount(bubble);
  };

  const count = { shown: readCount(), pending: null, freshAt: -Infinity };
  takeFreshRender();

  function getVariantId(source) {
    if (!source) return null;
    const form = source.closest('form');
    const input = form && form.querySelector('input[name="id"]');
    return input ? String(input.value || '') : (source.dataset.variantId || null);
  }

  function getReceiverRect() {
    const drawer = getDrawer();
    if (drawer && drawer.classList.contains('active')) {
      const mark = drawer.querySelector('[data-cc-cart-receiver]');
      if (mark) return mark.getBoundingClientRect();
      const header = drawer.querySelector('.drawer__header');
      if (header) return header.getBoundingClientRect();
    }

    const icon = document.getElementById('cart-icon-bubble');
    return icon ? icon.getBoundingClientRect() : null;
  }

  /* Where the Koh-i-Noor sits inside a bag drawing (the header icon or the
     drawer's bag mark — both use the bag's 100-unit geometry), and its radius. */
  function stoneIn(element) {
    const kohinoor = window.__fcKohinoor;
    const rect = element && element.getBoundingClientRect();
    if (!kohinoor || !rect || !rect.width) return null;
    return {
      x: rect.left + rect.width * kohinoor.stone.x / 100,
      y: rect.top + rect.height * kohinoor.stone.pivotY / 100,
      r: rect.width * kohinoor.stone.r / 100
    };
  }

  const headerStone = () => stoneIn(document.querySelector('#cart-icon-bubble [data-cc-cart-motion]'));

  function receiverStone() {
    const drawer = getDrawer();
    if (drawer && drawer.classList.contains('active')) {
      const mark = stoneIn(drawer.querySelector('[data-cc-cart-receiver] .cc-cart-static-bag'));
      if (mark) return mark;
    }
    return headerStone();
  }

  /* The bag's own Koh-i-Noor in flight: twice the size of the stone in the cart
     icon, turning on the icon's clock so it lands in step with the stone it
     becomes. It lifts off growing to full size, glides, then settles to the
     receiving stone's (swelling) size as it arrives. */
  function drawFlyingStone(ctx, x, y, raw, p, now, flight) {
    const kohinoor = window.__fcKohinoor;
    const { stone } = kohinoor;
    const grow = smoother(clamp(raw / .16));
    const settle = smoother(clamp((raw - .72) / .28));
    const r = mix(mix(flight.radius * .55, flight.radius, grow), flight.landingRadius, settle);
    const alpha = smoother(clamp(raw / .06)) * (1 - smoother(clamp((raw - .955) / .045)));
    if (alpha <= .001) return;

    const arc = Math.sin(Math.PI * p);
    const roll = mix(-.06, .04, p) + arc * .02;

    ctx.save();
    ctx.globalAlpha = alpha;

    /* A soft contact shadow below the stone gives it weight above the page. */
    const shadow = .15 * Math.sin(Math.PI * clamp(raw / .92));
    if (shadow > .004) {
      const sy = y + r * .95;
      const fall = ctx.createRadialGradient(x, sy, 0, x, sy, r * 1.05);
      fall.addColorStop(0, `rgba(40, 28, 12, ${shadow})`);
      fall.addColorStop(1, 'rgba(40, 28, 12, 0)');
      ctx.save();
      ctx.translate(x, sy);
      ctx.scale(1, .26);
      ctx.fillStyle = fall;
      ctx.beginPath();
      ctx.arc(0, 0, r * 1.05, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    /* (x, y) is the stone's visual centre; the renderer wants its girdle centre. */
    ctx.translate(x, y);
    ctx.rotate(roll);
    kohinoor.draw(ctx, {
      cx: 0,
      cy: (stone.y - stone.pivotY) / stone.r * r,
      r,
      spin: kohinoor.angleAt(now),
      tilt: stone.tilt + stone.nod * arc,
      /* One glint as it crests the arc. */
      sparkle: Math.sin(Math.PI * clamp((raw - .28) / .36)) * .85,
      /* Close to the icon's own light: brilliance is the contrast of lit and
         dark facets, which more exposure would wash out. */
      exposure: 1 + .1 * arc,
      edge: r / stone.r
    });
    ctx.restore();
  }

  function accentColor() {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-base-accent-1')
      .trim();

    const parts = raw.split(',').map((v) => parseFloat(v.trim())).filter(Number.isFinite);
    if (parts.length >= 3) return `rgb(${parts[0]}, ${parts[1]}, ${parts[2]})`;
    return 'rgb(219, 164, 170)';
  }

  function drawAlmond(ctx, x, y, rotation, scale, alpha, color) {
    if (alpha <= 0.001 || scale <= 0.001) return;

    const h = 35.2 * scale;
    const w = 23.4 * scale;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.globalAlpha = alpha;
    drawNailArt(ctx, w, h);
    ctx.restore();
  }

  function createFlightCanvas() {
    let canvas = document.querySelector('.cc-cart-flight');
    if (canvas) return canvas;

    canvas = document.createElement('canvas');
    canvas.className = 'cc-cart-flight';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);
    return canvas;
  }

  function sizeFlightCanvas(canvas) {
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    canvas.width = Math.round(window.innerWidth * dpr);
    canvas.height = Math.round(window.innerHeight * dpr);
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return null;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return ctx;
  }

  function triggerHeaderReceive() {
    const host = document.querySelector('[data-cc-cart-motion]');
    if (
      host
      && host.__ccCartMotion
      && typeof host.__ccCartMotion.triggerExternalReceive === 'function'
    ) {
      host.__ccCartMotion.triggerExternalReceive();
    }

    const drawer = getDrawer();
    if (drawer && drawer.classList.contains('active')) {
      drawer.classList.remove('cc-cart-drawer--receive');
      void drawer.offsetWidth;
      drawer.classList.add('cc-cart-drawer--receive');
      window.setTimeout(() => drawer.classList.remove('cc-cart-drawer--receive'), 1220);
    }
  }

  function beginFlight(source) {
    if (!source || (reduceMotion && reduceMotion.matches)) return;

    const from = source.getBoundingClientRect();
    const to = getReceiverRect();
    if (!to || !from.width || !from.height) return;

    const start = {
      x: from.left + from.width * .72,
      y: from.top + Math.min(from.height * .48, 30)
    };

    /* The flight lands on the stone inside the receiving bag; the nail (kept only
       as a fallback) lands on the bag as before. */
    const landing = receiverStone();
    const bagStone = headerStone() || landing;
    const stoneFlight = landing && bagStone ? {
      /* In flight the stone is four times the cart icon's stone. */
      radius: bagStone.r * 4,
      /* The receiving stone swells to about 1.2x as the flight arrives. */
      landingRadius: landing.r * 1.2
    } : null;

    const end = stoneFlight ? { x: landing.x, y: landing.y } : {
      x: to.left + to.width * .50,
      y: to.top + to.height * .52
    };

    const distance = Math.hypot(end.x - start.x, end.y - start.y);
    const duration = clamp(880 + distance * .24, 980, 1240) * 1.05;
    const startedAt = performance.now();

    state.blockDrawerUntil = startedAt + duration + 95;
    window.__ccCartFlightActive = true;
    window.__ccCartAmbientHoldUntil = Math.max(
      Number(window.__ccCartAmbientHoldUntil) || 0,
      startedAt + duration + 5200
    );

    /* Keep the flight in charge of cart-icon feedback until Shopify confirms
       the cart mutation; a timeout prevents a failed add from leaving stale state. */
    window.clearTimeout(window.__ccCartFlightFallbackTimer);
    window.__ccCartFlightFallbackTimer = window.setTimeout(() => {
      window.__ccCartFlightActive = false;
      commitPendingCount();
    }, 3200);

    const canvas = createFlightCanvas();
    const ctx = sizeFlightCanvas(canvas);
    if (!ctx) {
      canvas.remove();
      window.__ccCartFlightActive = false;
      window.clearTimeout(window.__ccCartFlightFallbackTimer);
      commitPendingCount();
      return;
    }
    const color = accentColor();

    const direction = end.x >= start.x ? 1 : -1;
    const lift = clamp(distance * .16, 34, 86);

    const p0 = start;
    const p1 = {
      x: start.x + direction * clamp(distance * .22, 32, 118),
      y: start.y - lift
    };
    const p2 = {
      x: end.x - direction * clamp(distance * .26, 36, 130),
      y: end.y - clamp(lift * .40, 18, 42)
    };
    const p3 = end;

    state.flight = { startedAt, duration, source };

    const frame = (now) => {
      if (!state.flight || state.flight.startedAt !== startedAt) return;

      const raw = clamp((now - startedAt) / duration);
      const p = smoother(raw);

      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      /* First 12% rises with purpose, then one unbroken Bézier glide. */
      const pos = cubic(p0, p1, p2, p3, p);
      const fadeIn = smoother(clamp(raw / .06));
      const fadeOut = 1 - smoother(clamp((raw - .955) / .045));
      const scale = mix(2.12, 1.46, smoother(clamp((raw - .16) / .84)));
      const rotation = mix(-.11, .065, p) + Math.sin(p * Math.PI) * .038;

      if (!state.flight.receiverTriggered && raw >= .60) {
        state.flight.receiverTriggered = true;
        triggerHeaderReceive();
      }

      if (stoneFlight) drawFlyingStone(ctx, pos.x, pos.y, raw, p, now, stoneFlight);
      else drawAlmond(ctx, pos.x, pos.y, rotation, scale, fadeIn * fadeOut, color);

      if (raw < 1) {
        requestAnimationFrame(frame);
      } else {
        ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
        state.flight = null;
        /* Do not leave an invisible full-viewport canvas promoted above the page.
           Android Chromium can retain expensive transparent compositor surfaces;
           removing this layer after the flight keeps the resting page clean. */
        canvas.remove();

        /* The stone has landed. Only now may the quantity badge advance — no
           snap into the number, just the count's own tactile, premium bounce. */
        window.__ccCartFlightActive = false;
        window.clearTimeout(window.__ccCartFlightFallbackTimer);
        window.setTimeout(() => commitPendingCount(), 42);
      }
    };

    requestAnimationFrame(frame);
  }

  function markAddStart(source) {
    if (!source || source.disabled || source.getAttribute('aria-disabled') === 'true') return;

    state.lastAddButton = source;
    state.pendingVariantId = getVariantId(source);
    state.keysBeforeAdd = currentKeys();
    state.addStartedAt = performance.now();
    state.suppressAutoOpen = true;

    window.clearTimeout(state.autoOpenSuppressionTimer);
    state.autoOpenSuppressionTimer = window.setTimeout(() => {
      state.suppressAutoOpen = false;
    }, 12000);

    beginFlight(source);

    window.clearTimeout(state.addIntentTimer);
    state.addIntentTimer = window.setTimeout(() => {
      state.lastAddButton = null;
      state.pendingVariantId = null;
      state.keysBeforeAdd = currentKeys();
    }, 4200);
  }

  /* Capture actual product-form submits. */
  document.addEventListener('submit', (event) => {
    const form = event.target && event.target.closest
      ? event.target.closest('form[data-type="add-to-cart-form"]')
      : null;

    if (!form) return;

    const source = event.submitter
      || form.querySelector('button[name="add"]')
      || form.querySelector('.atc-button');

    markAddStart(source);
  }, true);

  /* Fallback for AJAX add buttons that do not use Shopify product-form markup. */
  document.addEventListener('click', (event) => {
    const source = event.target.closest(
      '.quick-add__submit, .upsell__add-btn, [data-add-to-cart]'
    );
    if (!source || source.closest('form[data-type="add-to-cart-form"]')) return;
    markAddStart(source);
  }, true);


  /* Explicit cart-icon interaction is always allowed, even while an add request
     is still in flight. This distinguishes a shopper opening the cart from the
     theme's automatic post-add open call. */
  document.addEventListener('click', (event) => {
    if (event.target.closest('#cart-icon-bubble')) {
      state.manualOpenUntil = performance.now() + 1200;
    }
  }, true);

  document.addEventListener('keyup', (event) => {
    if (
      event.target.closest &&
      event.target.closest('#cart-icon-bubble') &&
      (event.key === 'Enter' || event.key === ' ')
    ) {
      state.manualOpenUntil = performance.now() + 1200;
    }
  }, true);

  const getRemovalSections = () => {
    const drawerItems = document.querySelector('cart-drawer-items');
    if (drawerItems && typeof drawerItems.getSectionsToRender === 'function') {
      return drawerItems.getSectionsToRender()
        .map((entry) => entry.section || entry.id)
        .filter(Boolean);
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  };

  const parseDrawerFromResponse = (data) => {
    const html = data && data.sections && data.sections['cart-drawer'];
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('#CartDrawer') || doc.querySelector('cart-drawer #CartDrawer');
  };

  /* A cart response's count: the bubble Shopify rendered with it when present
     (it applies the theme's own rules, e.g. "cart-hidden" items), else item_count. */
  const countFromResponse = (data) => {
    const html = data && data.sections && data.sections['cart-icon-bubble'];
    if (html) return readCount(new DOMParser().parseFromString(html, 'text/html').body);
    const n = Number(data && data.item_count);
    return Number.isFinite(n) ? Math.max(0, n) : null;
  };


  /* Copy a line's server state onto the line on screen. Positions and unit
     prices always follow Shopify; the quantity and the amounts only when
     `values` is true — i.e. when nothing newer is pending for this line. */
  const copyDynamicRowState = (currentRow, nextRow, values = true) => {
    if (!currentRow || !nextRow) return;

    currentRow.dataset.index = nextRow.dataset.index || currentRow.dataset.index;
    currentRow.id = nextRow.id || currentRow.id;

    const currentRemove = currentRow.querySelector('cart-remove-button');
    const nextRemove = nextRow.querySelector('cart-remove-button');
    if (currentRemove && nextRemove) {
      currentRemove.dataset.index = nextRemove.dataset.index || currentRemove.dataset.index;
      if (nextRemove.id) currentRemove.id = nextRemove.id;
    }

    const currentTotals = currentRow.querySelector('.cart-item__totals');
    const nextTotals = nextRow.querySelector('.cart-item__totals');
    if (currentTotals && nextTotals) {
      ['ccUnitFinal', 'ccUnitShown', 'ccUnitCompare', 'ccUnitCompareTotal'].forEach((name) => {
        if (nextTotals.dataset[name] !== undefined) currentTotals.dataset[name] = nextTotals.dataset[name];
      });
    }

    const currentInput = currentRow.querySelector('.quantity__input');
    const nextInput = nextRow.querySelector('.quantity__input');
    if (currentInput && nextInput) {
      currentInput.dataset.index = nextInput.dataset.index || currentInput.dataset.index;
      if (nextInput.id) currentInput.id = nextInput.id;
      if (values) {
        currentInput.value = nextInput.value;
        currentInput.defaultValue = nextInput.value;
      }
    }
    if (!values) return;

    const currentPrice = currentRow.querySelector('.cart-item__price-wrapper');
    const nextPrice = nextRow.querySelector('.cart-item__price-wrapper');
    if (currentPrice && nextPrice) currentPrice.innerHTML = nextPrice.innerHTML;

    const currentSaving = currentRow.querySelector('.cart-drawer-item__saving');
    const nextSaving = nextRow.querySelector('.cart-drawer-item__saving');
    if (currentSaving && nextSaving) currentSaving.innerHTML = nextSaving.innerHTML;
  };

  const syncDrawerTotals = (nextDrawerRoot, drawer = getDrawer()) => {
    if (!nextDrawerRoot || !drawer) return;
    const currentTotals = drawer.querySelector('.cart-drawer__totals');
    const nextTotals = nextDrawerRoot.querySelector('.cart-drawer__totals');
    if (currentTotals && nextTotals) currentTotals.innerHTML = nextTotals.innerHTML;

    const currentSavings = drawer.querySelector('.cart-drawer__total-savings');
    const nextSavings = nextDrawerRoot.querySelector('.cart-drawer__total-savings');
    if (currentSavings && nextSavings) currentSavings.innerHTML = nextSavings.innerHTML;
  };

  const installEmptyStateFromServer = (nextDrawerRoot, drawer = getDrawer()) => {
    if (!nextDrawerRoot || !drawer) return;
    const inner = drawer.querySelector('.drawer__inner');
    const nextEmpty = nextDrawerRoot.querySelector('.drawer__inner-empty');
    if (!inner || !nextEmpty) return;

    const currentEmpty = inner.querySelector('.drawer__inner-empty');
    if (currentEmpty) currentEmpty.remove();

    const clone = nextEmpty.cloneNode(true);
    inner.insertBefore(clone, inner.firstChild);
    drawer.classList.add('is-empty');
    const drawerItems = drawer.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.classList.add('is-empty');

    /* Immediately freeze the no-longer-relevant populated layers. CSS also
       enforces this, but inline state prevents a single paint of old content. */
    ['.drawer__header', '.cart-drawer__body', '.drawer__footer'].forEach((selector) => {
      const node = inner.querySelector(selector);
      if (node) node.setAttribute('aria-hidden', 'true');
    });
  };

  const animatePremiumRemoval = async (row) => {
    const drawer = getDrawer();
    if (!row || !drawer) return;

    if (reduceMotion && reduceMotion.matches) {
      row.style.display = 'none';
      return;
    }

    /* Keep the real line out of the animation. We animate a visual clone while
       the real row is removed from layout in the SAME frame. This prevents the
       left/right flicker caused by layout + transform competing on one node. */
    const rect = row.getBoundingClientRect();
    const siblings = Array.from(drawer.querySelectorAll('#CartDrawer-CartItems .cart-drawer-item'))
      .filter((item) => item !== row && item.dataset.ccRemoving !== 'true');
    const before = new Map(siblings.map((item) => [item, item.getBoundingClientRect().top]));

    const ghost = row.cloneNode(true);
    ghost.classList.add('cc-cart-removal-ghost');
    ghost.removeAttribute('id');
    ghost.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    Object.assign(ghost.style, {
      position: 'fixed',
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      margin: '0',
      zIndex: '2147482500',
      pointerEvents: 'none',
      transformOrigin: '50% 42%'
    });
    drawer.appendChild(ghost);

    row.classList.add('cc-cart-item--removing');
    row.style.display = 'none';

    /* Because the layout has already collapsed but the browser has not painted
       yet, surviving rows can be inverted immediately and then glide only on Y. */
    const rowAnimations = siblings.map((item) => {
      if (!item.isConnected) return null;
      const nextTop = item.getBoundingClientRect().top;
      const dy = (before.get(item) || nextTop) - nextTop;
      if (Math.abs(dy) < .5) return null;

      return item.animate(
        [
          { transform: `translate3d(0, ${dy}px, 0)` },
          { transform: 'translate3d(0, 0, 0)' }
        ],
        {
          duration: 410,
          easing: 'cubic-bezier(.16,1,.30,1)',
          fill: 'both'
        }
      );
    }).filter(Boolean);

    const ghostAnimation = ghost.animate(
      [
        { opacity: 1, transform: 'translate3d(0,0,0) scale(1)', offset: 0 },
        { opacity: 1, transform: 'translate3d(0,-1px,0) scale(.998)', offset: .30 },
        { opacity: .94, transform: 'translate3d(0,-3px,0) scale(.992)', offset: .68 },
        { opacity: 0, transform: 'translate3d(0,-7px,0) scale(.978)', offset: 1 }
      ],
      {
        duration: 285,
        easing: 'cubic-bezier(.20,.78,.24,1)',
        fill: 'both'
      }
    );

    await Promise.all([
      ghostAnimation.finished.catch(() => {}),
      ...rowAnimations.map((animation) => animation.finished.catch(() => {}))
    ]);

    ghost.remove();
    rowAnimations.forEach((animation) => {
      try { animation.cancel(); } catch (_) {}
    });
  };

  /* ---------- Quantity and removal ----------
     A tap changes the line at once: its quantity, its total, the savings, the
     subtotal and the bag count are recalculated here from the unit prices
     Shopify rendered into the line (data-cc-unit-*). Shopify is then told each
     line's latest wanted quantity, addressed by its line-item key, through one
     queue: a single cart request at a time, so Shopify applies changes in the
     order they were made and every answer is the cart's newest state. A queued
     line sends its quantity as wanted when its turn comes, so a burst of taps
     becomes one request and none is lost or undone by an older answer.
     A line is repainted from an answer only when nothing newer is pending for
     it; while anything is pending, totals are the answer's plus the pending
     differences. Below the smallest allowed quantity a line is removed. */
  const QUANTITY_SETTLE_MS = 220;
  const lines = new Map();
  const totalsBase = { subtotal: null, compare: null, count: null, root: null };
  let sentSeq = 0;
  let appliedSeq = 0;

  /* Amounts in the store's own money format, learned from a sample Shopify
     rendered (123456789 in the smallest unit): symbol, separators, grouping. */
  let moneyFormatter;
  const formatMoney = (cents, trimZeros = false) => {
    if (moneyFormatter === undefined) {
      moneyFormatter = null;
      const sample = (getDrawer() && getDrawer().dataset.ccMoney) || '';
      const match = sample.match(/^([^0-9]*)([0-9][0-9.,'\s  ]*[0-9])([^0-9]*)$/);
      if (match) {
        const [, prefix, core, suffix] = match;
        const decimal = /[.,]\d{2}$/.test(core) ? core.slice(-3, -2) : '';
        const whole = decimal ? core.slice(0, -3) : core;
        const separator = whole.replace(/\d/g, '').charAt(0);
        const groups = separator ? whole.split(separator).map((g) => g.length) : [whole.length];
        const indian = groups.length > 2 && groups[groups.length - 1] === 3 && groups.slice(1, -1).every((g) => g === 2);
        moneyFormatter = { prefix, suffix, decimal, separator, indian };
      }
    }
    if (!moneyFormatter) return null;
    const { prefix, suffix, decimal, separator, indian } = moneyFormatter;
    const value = Math.abs(Math.round(cents));
    let units = String(decimal ? Math.floor(value / 100) : Math.round(value / 100));
    if (separator) {
      units = indian && units.length > 3
        ? units.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, separator) + separator + units.slice(-3)
        : units.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    }
    const fraction = String(value % 100).padStart(2, '0');
    const amount = decimal && !(trimZeros && fraction === '00') ? units + decimal + fraction : units;
    return `${cents < 0 ? '-' : ''}${prefix}${amount}${suffix}`;
  };

  const rowByKey = (key) => {
    const drawer = getDrawer();
    return drawer && drawer.querySelector(`#CartDrawer-CartItems [data-cc-cart-key="${CSS.escape(key)}"]`);
  };

  const unitsOf = (row) => {
    const data = (row && row.querySelector('.cart-item__totals') || {}).dataset || {};
    const read = (value) => (value === undefined || value === '' || !Number.isFinite(Number(value)) ? null : Number(value));
    return {
      final: read(data.ccUnitFinal),
      shown: read(data.ccUnitShown),
      compare: read(data.ccUnitCompare),
      compareTotal: read(data.ccUnitCompareTotal)
    };
  };

  const quantityOf = (row) => {
    const input = row && row.querySelector('.quantity__input');
    return Math.max(0, parseInt(input && input.value, 10) || 0);
  };

  function lineFor(row) {
    const key = row && row.dataset.ccCartKey;
    if (!key) return null;
    let line = lines.get(key);
    if (!line) {
      const quantity = quantityOf(row);
      line = { key, desired: quantity, snapshot: quantity, units: unitsOf(row), timer: 0, queued: false, sending: false, removing: false, exit: null };
      lines.set(key, line);
    }
    return line;
  }

  const lineIdle = (line) => !line.timer && !line.queued && !line.sending && !line.removing && line.desired === line.snapshot;
  const anythingPending = () => Array.from(lines.values()).some((line) => !lineIdle(line));

  const quantityErrorText = (quantity) =>
    ((window.cartStrings && window.cartStrings.quantityError) || 'You can only add [quantity] of this item to your cart.')
      .replace('[quantity]', quantity)
      .replace(/<[^>]*>/g, '');

  function lineMessage(key, text) {
    const row = rowByKey(key);
    const node = row && row.querySelector('.cart-item__error-text');
    if (!node) return;
    window.clearTimeout(node.__ccMessageTimer);
    node.textContent = text || '';
    if (text) node.__ccMessageTimer = window.setTimeout(() => { node.textContent = ''; }, 5000);
  }

  /* The line on screen: its quantity, its total, its struck price and savings. */
  function paintLine(row, line) {
    const input = row.querySelector('.quantity__input');
    if (input && String(input.value) !== String(line.desired)) input.value = String(line.desired);
    /* Minus is never "disabled" in the drawer: at the last unit it removes. */
    const minus = row.querySelector('.quantity__button--minus');
    if (minus) minus.classList.remove('disabled');
    const max = parseInt(input && input.max, 10);
    const plus = row.querySelector('.quantity__button--plus');
    if (plus) plus.classList.toggle('disabled', Number.isFinite(max) && line.desired >= max);

    const { shown, compare, final } = line.units;
    const wrapper = row.querySelector('.cart-item__price-wrapper');
    if (!wrapper || formatMoney(0) === null) return;
    const finalNode = wrapper.querySelector('.regular-price');
    if (finalNode && shown !== null) finalNode.textContent = formatMoney(shown * line.desired);
    const oldNode = wrapper.querySelector('.cart-item__old-price');
    if (oldNode && compare) oldNode.textContent = formatMoney(compare * line.desired);
    const saving = row.querySelector('.cart-drawer-item__saving[data-cc-template]');
    if (saving && compare && final !== null) {
      saving.innerHTML = saving.dataset.ccTemplate.replace('[amount]', formatMoney((compare - final) * line.desired, true));
    }
  }

  /* Subtotal, savings and the bag count. Exact from Shopify when every line
     agrees with it; otherwise Shopify's last snapshot plus what is pending. */
  function paintTotals() {
    const drawer = getDrawer();
    if (!drawer) return;

    if (!anythingPending() && totalsBase.root) {
      syncDrawerTotals(totalsBase.root, drawer);
      if (totalsBase.count !== null) receiveCount(totalsBase.count);
      return;
    }

    if (totalsBase.subtotal === null) {
      const subtotal = drawer.querySelector('[data-cc-total="subtotal"]');
      if (subtotal) {
        totalsBase.subtotal = Number(subtotal.dataset.ccCents);
        totalsBase.compare = Number(subtotal.dataset.ccCompareCents);
      }
    }
    if (totalsBase.count === null) totalsBase.count = count.shown;

    let subtotalDelta = 0;
    let compareDelta = 0;
    let countDelta = 0;
    for (const line of lines.values()) {
      const difference = line.desired - line.snapshot;
      if (!difference) continue;
      subtotalDelta += difference * (line.units.final || 0);
      compareDelta += difference * (line.units.compareTotal || 0);
      countDelta += difference;
    }

    if (Number.isFinite(totalsBase.subtotal) && formatMoney(0) !== null) {
      const subtotal = totalsBase.subtotal + subtotalDelta;
      drawer.querySelectorAll('[data-cc-total="subtotal"]').forEach((node) => { node.textContent = formatMoney(subtotal); });
      if (Number.isFinite(totalsBase.compare)) {
        const savings = totalsBase.compare + compareDelta - subtotal;
        drawer.querySelectorAll('[data-cc-total="savings"]').forEach((node) => {
          const row = node.closest('.cart-drawer__totals__row');
          if (savings > 0) {
            node.textContent = formatMoney(savings);
            if (row) row.style.removeProperty('display');
          } else if (row) {
            row.style.display = 'none';
          }
        });
      }
    }

    const predicted = Math.max(0, totalsBase.count + countDelta);
    if (predicted !== count.shown && window.__ccCartFlightActive !== true) displayCount(predicted);
  }

  /* Apply one of Shopify's answers (a snapshot of the whole cart). */
  function applySnapshot(data, seq) {
    const drawer = getDrawer();
    const nextRoot = parseDrawerFromResponse(data);
    if (!drawer || !nextRoot || seq < appliedSeq) return false;
    appliedSeq = seq;

    const nextRows = new Map();
    nextRoot.querySelectorAll('#CartDrawer-CartItems [data-cc-cart-key]').forEach((row) => nextRows.set(row.dataset.ccCartKey, row));

    drawer.querySelectorAll('#CartDrawer-CartItems [data-cc-cart-key]').forEach((row) => {
      const line = lineFor(row);
      if (!line) return;
      const next = nextRows.get(line.key);
      line.snapshot = next ? quantityOf(next) : 0;
      if (!line.timer && !line.queued && !line.sending && !line.removing && line.desired !== line.snapshot) {
        /* Shopify's cart changed under a settled line (another tab, an app, a
           rule on Shopify's side): the screen follows Shopify. */
        line.desired = line.snapshot;
        if (!next) {
          line.removing = true;
          row.dataset.ccRemoving = 'true';
          line.exit = animatePremiumRemoval(row);
        }
      }
      if (!next) return;
      line.units = unitsOf(next);
      copyDynamicRowState(row, next, lineIdle(line));
    });

    const subtotal = nextRoot.querySelector('[data-cc-total="subtotal"]');
    totalsBase.subtotal = subtotal ? Number(subtotal.dataset.ccCents) : null;
    totalsBase.compare = subtotal ? Number(subtotal.dataset.ccCompareCents) : null;
    totalsBase.count = countFromResponse(data);
    totalsBase.root = nextRoot;
    /* An authoritative answer: no separate count check is needed for it. */
    count.freshAt = performance.now();

    paintTotals();
    finishRemovals(nextRoot);
    return true;
  }

  /* Take the cart's real state straight from Shopify (queued like any change). */
  const refreshSnapshot = () => enqueue(REFRESH);

  async function fetchSnapshot() {
    const seq = ++sentSeq;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}?sections=${getRemovalSections().join(',')}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      applySnapshot({ sections: await response.json() }, seq);
    } catch (error) {
      console.warn('Fifth Collective: cart refresh failed', error);
    }
  }

  /* Lines Shopify no longer has leave the screen once their exit has played;
     the last one gives way to the empty bag. */
  async function finishRemovals(nextRoot) {
    const gone = Array.from(lines.values()).filter((line) => line.removing && line.snapshot === 0 && !line.sending);
    for (const line of gone) {
      lines.delete(line.key);
      const row = rowByKey(line.key);
      await line.exit;
      if (row && row.isConnected) row.remove();
    }
    if (!gone.length) return;
    state.keysBeforeAdd = currentKeys();
    const drawer = getDrawer();
    if (drawer && !nextRoot.querySelector('#CartDrawer-CartItems [data-cc-cart-key]') && !getRows().length) {
      installEmptyStateFromServer(nextRoot, drawer);
      requestAnimationFrame(() => {
        animateEmptyState(drawer);
        state.emptyAnimationRun = true;
      });
    }
  }

  function restoreRemovedRow(line) {
    const row = rowByKey(line.key);
    line.removing = false;
    if (!row) return;
    row.style.display = '';
    row.dataset.ccRemoving = 'false';
    row.classList.remove('cc-cart-item--removing', 'cc-cart-item--removed-optimistic');
    row.getAnimations().forEach((animation) => animation.cancel());
    row.animate(
      [
        { opacity: 0, transform: 'translate3d(0,-3px,0) scale(.985)' },
        { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' }
      ],
      { duration: 220, easing: 'cubic-bezier(.16,1,.3,1)' }
    );
    const remove = row.querySelector('cart-remove-button');
    if (remove) remove.removeAttribute('aria-disabled');
    paintLine(row, line);
  }

  /* One cart request at a time, in the order the changes were made. */
  const REFRESH = { queued: false };
  const queue = [];
  let queueBusy = false;

  function enqueue(item) {
    if (!item.queued) {
      item.queued = true;
      queue.push(item);
    }
    pumpQueue();
  }

  async function pumpQueue() {
    if (queueBusy) return;
    const item = queue.shift();
    if (!item) return;
    item.queued = false;
    queueBusy = true;
    try {
      if (item === REFRESH) await fetchSnapshot();
      else await sendLine(item);
    } catch (error) {
      console.error(error);
    } finally {
      queueBusy = false;
      pumpQueue();
    }
  }

  function scheduleLine(line, delay) {
    window.clearTimeout(line.timer);
    line.timer = window.setTimeout(() => {
      line.timer = 0;
      enqueue(line);
    }, delay);
  }

  /* Tell Shopify a line's quantity as wanted right now (its turn in the queue). */
  async function sendLine(line) {
    if (lines.get(line.key) !== line) return;
    if (line.desired === line.snapshot) {
      if (!line.removing) {
        const row = rowByKey(line.key);
        const next = totalsBase.root && totalsBase.root.querySelector(`#CartDrawer-CartItems [data-cc-cart-key="${CSS.escape(line.key)}"]`);
        if (row && next && lineIdle(line)) copyDynamicRowState(row, next, true);
      }
      paintTotals();
      if (line.removing && totalsBase.root) finishRemovals(totalsBase.root);
      return;
    }

    line.sending = true;
    const quantity = line.desired;
    const seq = ++sentSeq;
    let data = null;
    let problem = '';
    try {
      const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: line.key,
          quantity,
          sections: getRemovalSections(),
          sections_url: window.location.pathname + window.location.search
        })
      });
      data = await response.json().catch(() => null);
      if (!response.ok || !data) {
        problem = (data && (data.description || data.message)) || (window.cartStrings && window.cartStrings.error) || 'There was an error while updating your cart. Please try again.';
      }
    } catch (_) {
      problem = (window.cartStrings && window.cartStrings.error) || 'There was an error while updating your cart. Please try again.';
    }

    if (problem) {
      line.sending = false;
      /* Shopify refused or could not be reached: say so, return the line to
         Shopify's last known quantity and confirm the real state. */
      lineMessage(line.key, String(problem).replace(/<[^>]*>/g, ''));
      window.clearTimeout(line.timer);
      line.timer = 0;
      line.desired = line.snapshot;
      if (line.removing) restoreRemovedRow(line);
      else {
        const row = rowByKey(line.key);
        if (row) paintLine(row, line);
      }
      paintTotals();
      refreshSnapshot();
      return;
    }

    /* Still "sending" while the answer is applied, so this line's own handling
       below — not the general follow-Shopify rule — decides about it. */
    applySnapshot(data, seq);
    line.sending = false;
    if (lines.get(line.key) !== line) return;

    if (!line.timer && !line.queued && line.desired === quantity && line.snapshot !== quantity) {
      /* Shopify kept another quantity than asked (stock, a rule) and no tap
         came since: the screen follows Shopify and says why. */
      line.desired = line.snapshot;
      if (line.removing && line.snapshot > 0) restoreRemovedRow(line);
      const row = rowByKey(line.key);
      if (row && !line.removing && line.snapshot > 0 && line.snapshot < quantity) {
        lineMessage(line.key, quantityErrorText(line.snapshot));
      }
    }

    /* Settled: show Shopify's exact line and, if nothing else is pending, totals. */
    if (lineIdle(line)) {
      const row = rowByKey(line.key);
      const next = totalsBase.root && totalsBase.root.querySelector(`#CartDrawer-CartItems [data-cc-cart-key="${CSS.escape(line.key)}"]`);
      if (row && next) copyDynamicRowState(row, next, true);
    } else if (!line.removing) {
      const row = rowByKey(line.key);
      if (row) paintLine(row, line);
    }
    paintTotals();
    if (line.removing && totalsBase.root) finishRemovals(totalsBase.root);
  }

  function resetSettledLines() {
    for (const line of Array.from(lines.values())) {
      if (lineIdle(line)) {
        lines.delete(line.key);
        continue;
      }
      const row = rowByKey(line.key);
      if (row && !line.removing) paintLine(row, line);
    }
    totalsBase.subtotal = null;
    totalsBase.compare = null;
    totalsBase.count = null;
    totalsBase.root = null;
  }

  function setDesired(row, line, quantity) {
    lineMessage(line.key, '');
    if (quantity === line.desired) return;
    line.desired = quantity;
    paintLine(row, line);
    scheduleLine(line, QUANTITY_SETTLE_MS);
    paintTotals();
  }

  function removeLine(row, line) {
    if (!row || !line || line.removing) return;
    line.removing = true;
    line.desired = 0;
    window.clearTimeout(line.timer);
    line.timer = 0;
    lineMessage(line.key, '');
    row.dataset.ccRemoving = 'true';
    const remove = row.querySelector('cart-remove-button');
    if (remove) remove.setAttribute('aria-disabled', 'true');
    /* A checkout submitted this instant sends 0 for this line. */
    const input = row.querySelector('.quantity__input');
    if (input) input.value = '0';
    line.exit = animatePremiumRemoval(row);
    paintTotals();
    enqueue(line);
  }

  /* Quantity buttons: handled here, before the theme's stepper (which stops at
     its minimum and repaints the whole drawer per tap). */
  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('cart-drawer .cart-drawer-item .quantity__button');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    const row = button.closest('.cart-drawer-item');
    const input = row && row.querySelector('.quantity__input');
    if (!row || !input || row.dataset.ccRemoving === 'true') return;

    const line = lineFor(row);
    if (!line) return;
    const step = Math.max(1, parseInt(input.step, 10) || 1);
    const min = Math.max(1, parseInt(input.min, 10) || 1);
    const max = parseInt(input.max, 10);
    let quantity = line.desired + (button.name === 'plus' ? step : -step);
    if (quantity < min) {
      removeLine(row, line);
      return;
    }
    if (Number.isFinite(max) && quantity > max) {
      quantity = max;
      lineMessage(line.key, quantityErrorText(max));
    }
    setDesired(row, line, quantity);
  }, true);

  /* A quantity typed into the field. */
  document.addEventListener('change', (event) => {
    const input = event.target.closest && event.target.closest('cart-drawer .quantity__input');
    if (!input) return;
    event.stopPropagation();
    event.stopImmediatePropagation();
    const row = input.closest('.cart-drawer-item');
    const line = lineFor(row);
    if (!row || !line || row.dataset.ccRemoving === 'true') return;

    const typed = parseInt(input.value, 10);
    const min = Math.max(1, parseInt(input.min, 10) || 1);
    const max = parseInt(input.max, 10);
    if (!Number.isFinite(typed)) {
      input.value = String(line.desired);
      return;
    }
    if (typed < min) {
      removeLine(row, line);
      return;
    }
    const quantity = Number.isFinite(max) ? Math.min(typed, max) : typed;
    if (quantity !== typed) lineMessage(line.key, quantityErrorText(max));
    setDesired(row, line, quantity);
  }, true);

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('cart-remove-button, .cart-drawer-item__cart-remove-button');
    if (!remove) return;
    const row = remove.closest('.cart-drawer-item');
    if (!row) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    removeLine(row, lineFor(row));
  }, true);

  function getCountTransitionDuration() {
    const receiveStart = Number(window.__ccCartReceiveStartedAt);
    const receiveDuration = Number(window.__ccCartReceiveDuration);

    if (Number.isFinite(receiveStart) && Number.isFinite(receiveDuration)) {
      const remaining = (receiveStart + receiveDuration) - performance.now();
      /* End just before the enlarged bag becomes perfectly still. */
      return Math.round(clamp(remaining - 26, 650, 980));
    }

    return 820;
  }

  /* Animate the badge from `previous` to `next` (the badge exists). */
  function runCountTransition(previous, next) {
    const badge = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    count.shown = next;
    if (!badge) return;

    const number = badge.querySelector('span[aria-hidden="true"]');
    if (number) number.textContent = String(next);

    const duration = getCountTransitionDuration();
    badge.style.setProperty('--cc-count-duration', `${duration}ms`);
    badge.dataset.ccOldCount = previous > 0 ? String(previous) : '';
    badge.classList.remove(
      'cc-cart-count--held',
      'cc-cart-count--changing',
      'cc-cart-count--first'
    );
    void badge.offsetWidth;

    /* This is deliberately a number/badge-only response. No icon or canvas is
       created here. */
    badge.classList.add(previous === 0 && next > 0 ? 'cc-cart-count--first' : 'cc-cart-count--changing');

    window.setTimeout(() => {
      badge.classList.remove('cc-cart-count--changing', 'cc-cart-count--first');
      badge.removeAttribute('data-cc-old-count');
      badge.style.removeProperty('--cc-count-duration');
    }, duration + 90);
  }

  /* Put `next` on the badge now: animate the change, fade the badge away at
     zero, or — when there is no badge to show it on — fetch Shopify's own markup
     for one (it then arrives as a fresh render). */
  function displayCount(next) {
    const badge = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!badge) {
      if (next > 0) requestReconcile(0, true);
      else count.shown = 0;
      return;
    }

    badge.classList.remove('cc-cart-count--held');
    ['opacity', 'transform', 'transition'].forEach((property) => badge.style.removeProperty(property));

    if (next === 0) {
      count.shown = 0;
      badge.style.transition = 'opacity 110ms ease, transform 110ms ease';
      badge.style.opacity = '0';
      badge.style.transform = 'scale(.86)';
      window.setTimeout(() => {
        if (count.shown === 0 && badge.isConnected) badge.remove();
      }, 130);
      return;
    }

    if (next === count.shown) {
      const number = badge.querySelector('span[aria-hidden="true"]');
      if (number && number.textContent.trim() !== String(next)) number.textContent = String(next);
      return;
    }

    runCountTransition(count.shown, next);
  }

  /* While the stone is still in flight, a freshly rendered bubble keeps showing
     the number it had before. */
  function holdDisplayed() {
    const badge = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!badge) return;
    if (count.shown > 0) {
      const number = badge.querySelector('span[aria-hidden="true"]');
      if (number) number.textContent = String(count.shown);
    } else {
      /* First piece: the new badge stays out of sight until the stone lands,
         then grows in on its own. */
      badge.classList.add('cc-cart-count--held');
    }
  }

  /* Every authoritative count — a fresh Shopify render or a cart response —
     enters here. During the add-to-cart flight it waits for the landing. */
  function receiveCount(next) {
    count.freshAt = performance.now();
    if (window.__ccCartFlightActive === true) {
      count.pending = next;
      if (next !== count.shown) holdDisplayed();
      return;
    }
    count.pending = null;
    displayCount(next);
  }

  function commitPendingCount() {
    if (count.pending === null) return;
    const next = count.pending;
    count.pending = null;
    displayCount(next);
  }

  /* ---------- Keeping the badge live ----------
     Shopify re-renders the bubble after the theme's own cart requests. Anything
     else that changes the cart — an app posting to /cart/add.js, another tab, a
     page restored from the back/forward cache — is caught here: the bubble is
     re-rendered through Shopify's Section Rendering API and then flows through
     the same path as any other render. */
  const reconcile = { timer: 0, controller: null, lastRunAt: -Infinity };

  function requestReconcile(delay = 260, force = false) {
    const requestedAt = performance.now();
    window.clearTimeout(reconcile.timer);
    reconcile.timer = window.setTimeout(() => {
      /* Shopify already delivered a newer count after this request. */
      if (!force && count.freshAt > requestedAt) return;
      /* While drawer lines are still being sent, Shopify's count is behind the
         screen by design; the drawer delivers the exact count once they settle. */
      if (anythingPending()) {
        requestReconcile(400, force);
        return;
      }
      runReconcile();
    }, delay);
  }

  async function runReconcile() {
    if (reconcile.controller) reconcile.controller.abort();
    const controller = new AbortController();
    reconcile.controller = controller;
    reconcile.lastRunAt = performance.now();

    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}?sections=cart-icon-bubble`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) return;
      const data = await response.json();
      const html = data && data['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (!html || !bubble) return;

      const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (!section) return;
      const next = readCount(section);
      const target = count.pending !== null ? count.pending : count.shown;
      const hasBadge = !!bubble.querySelector('.cart-count-bubble');
      if (next === target && hasBadge === next > 0) return;

      /* The same swap Shopify's own cart renderer performs. */
      bubble.innerHTML = section.innerHTML;
    } catch (error) {
      if (!error || error.name !== 'AbortError') console.warn('Fifth Collective: cart count refresh failed', error);
    } finally {
      if (reconcile.controller === controller) reconcile.controller = null;
    }
  }

  /* Any completed cart request, from the theme or an app (fetch or XHR). */
  const CART_REQUEST = /\/cart\/(?:add|change|update|clear)(?:\.js)?(?:[?#]|$)/;
  if (typeof PerformanceObserver === 'function') {
    try {
      new PerformanceObserver((list) => {
        if (list.getEntries().some((entry) => CART_REQUEST.test(entry.name))) requestReconcile();
      }).observe({ type: 'resource' });
    } catch (_) {
      /* Resource timing unavailable: the theme's own renders still keep it live. */
    }
  }

  /* A page restored from the back/forward cache shows the count from when it was
     left; returning to the tab may follow a change made in another tab. */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) requestReconcile(0, true);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && performance.now() - reconcile.lastRunAt > 4000) {
      requestReconcile(0, true);
    }
  });

  function flagArrivingItem() {
    const rows = getRows();
    if (!rows.length) return;

    let arriving = rows.find((row) => {
      const key = row.dataset.ccCartKey;
      return key && !state.keysBeforeAdd.has(key);
    });

    if (!arriving && state.pendingVariantId) {
      arriving = rows.find((row) => row.dataset.ccVariantId === String(state.pendingVariantId));
    }

    if (!arriving) arriving = rows[0];
    if (!arriving) return;

    arriving.classList.remove('cc-cart-item--arriving');
    void arriving.offsetWidth;
    arriving.classList.add('cc-cart-item--arriving');

    window.setTimeout(() => {
      arriving && arriving.classList.remove('cc-cart-item--arriving');
    }, 1050);

    state.keysBeforeAdd = currentKeys();
    state.pendingVariantId = null;
  }

  /* Empty Collective Bag: the house jewellery box turns, opens and closes
     (assets/fifth-collective-jewellery-box.js, fetched the first time it is needed).
     It keeps playing while the empty drawer is open and stops when it closes. */
  let jewelleryBoxScript = null;
  const loadJewelleryBox = (src) => {
    if (window.__fcJewelleryBox) return Promise.resolve(window.__fcJewelleryBox);
    if (!src) return Promise.reject(new Error('jewellery box script not set'));
    if (!jewelleryBoxScript) {
      jewelleryBoxScript = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => (window.__fcJewelleryBox ? resolve(window.__fcJewelleryBox) : reject(new Error('jewellery box missing')));
        script.onerror = () => {
          jewelleryBoxScript = null;
          reject(new Error('jewellery box failed to load'));
        };
        document.head.appendChild(script);
      });
    }
    return jewelleryBoxScript;
  };

  function animateEmptyState(drawer) {
    const canvas = drawer && drawer.querySelector('.cc-cart-empty__canvas');
    if (!canvas || canvas.__ccJewelleryBox) return;
    const playing = { player: null, stopped: false };
    canvas.__ccJewelleryBox = playing;
    loadJewelleryBox(canvas.dataset.ccBoxSrc)
      .then((box) => {
        if (playing.stopped || !canvas.isConnected) return;
        playing.player = box.play(canvas, { reducedMotion: !!(reduceMotion && reduceMotion.matches) });
      })
      .catch((error) => {
        if (canvas.__ccJewelleryBox === playing) canvas.__ccJewelleryBox = null;
        console.warn('Fifth Collective: empty bag visual unavailable', error);
      });
  }

  function stopEmptyState(drawer) {
    const canvas = drawer && drawer.querySelector('.cc-cart-empty__canvas');
    const playing = canvas && canvas.__ccJewelleryBox;
    if (!playing) return;
    playing.stopped = true;
    if (playing.player) playing.player.stop();
    canvas.__ccJewelleryBox = null;
  }


  /* Luxury drawer layout continuity. Shopify replaces cart section HTML after
     quantity/remove/add updates; preserve the old row positions and interpolate
     into the new ones so the interface never appears to jump. */
  const captureDrawerLayout = (drawer = getDrawer()) => {
    const snapshot = new Map();
    if (!drawer) return snapshot;

    drawer.querySelectorAll('#CartDrawer-CartItems [data-cc-cart-key]').forEach((row) => {
      const key = row.dataset.ccCartKey;
      if (!key) return;
      const rect = row.getBoundingClientRect();
      snapshot.set(key, { top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    });
    return snapshot;
  };

  const animateDrawerReflow = (before, drawer = getDrawer()) => {
    if (!drawer || !before || (reduceMotion && reduceMotion.matches)) return;

    drawer.querySelectorAll('#CartDrawer-CartItems [data-cc-cart-key]').forEach((row) => {
      const key = row.dataset.ccCartKey;
      const previous = key && before.get(key);
      if (!previous) return;

      const next = row.getBoundingClientRect();
      const dy = previous.top - next.top;
      if (Math.abs(dy) < .5) return;

      row.animate(
        [
          { transform: `translate3d(0, ${dy}px, 0)` },
          { transform: 'translate3d(0, 0, 0)' }
        ],
        {
          duration: 410,
          easing: 'cubic-bezier(.16,1,.30,1)',
          fill: 'both'
        }
      );
    });
  };

  const pulseDrawerData = (drawer = getDrawer()) => {
    if (!drawer || (reduceMotion && reduceMotion.matches)) return;
    drawer.classList.remove('cc-cart-data-refreshed');
    void drawer.offsetWidth;
    drawer.classList.add('cc-cart-data-refreshed');
    window.setTimeout(() => drawer.classList.remove('cc-cart-data-refreshed'), 460);
  };

  document.addEventListener('pointerdown', (event) => {
    const button = event.target.closest && event.target.closest('cart-drawer .quantity__button');
    if (!button) return;
    const row = button.closest('.cart-drawer-item');
    if (!row) return;
    row.classList.remove('cc-cart-item--quantity-touch');
    void row.offsetWidth;
    row.classList.add('cc-cart-item--quantity-touch');
    window.setTimeout(() => row.classList.remove('cc-cart-item--quantity-touch'), 380);
  }, true);


  /* ---------------- Fifth Collective Silk Glide ----------------
     Opening/closing are intentionally simple: one panel transform + opacity
     and one overlay opacity. No clip-path, no geometry reads, no reveal shell,
     no staggered child animations. This is fast and GPU-friendly on iOS,
     Android and desktop. */
  const CC_SILK_OPEN_MS = 430;
  const CC_SILK_CLOSE_MS = 335;
  const silkTimers = new WeakMap();

  const clearSilkTimer = (drawer) => {
    const timer = silkTimers.get(drawer);
    if (timer) window.clearTimeout(timer);
    silkTimers.delete(drawer);
  };

  /* Prevent automatic drawer opening after Add to Cart at the source.
     The theme calls cart.open() and then cart.renderContents(). We suppress the
     automatic open and force renderContents(..., true), which still updates the
     cart HTML/count but skips opening the drawer. This remains reliable even on
     slow networks; it is not dependent on a short timing window. */
  customElements.whenDefined('cart-drawer').then(() => {
    const CartDrawer = customElements.get('cart-drawer');
    if (!CartDrawer || CartDrawer.prototype.__ccOpenPatched) return;

    const originalOpen = CartDrawer.prototype.open;
    const originalClose = CartDrawer.prototype.close;
    const originalRenderContents = CartDrawer.prototype.renderContents;

    if (typeof originalOpen !== 'function' || typeof originalRenderContents !== 'function') return;

    CartDrawer.prototype.__ccOpenPatched = true;

    CartDrawer.prototype.open = function(...args) {
      const manualOpen = performance.now() < state.manualOpenUntil;
      if (state.suppressAutoOpen && !manualOpen) return;
      if (this.classList.contains('active') && !this.classList.contains('cc-cart-closing')) return;

      clearSilkTimer(this);
      this.classList.remove('cc-cart-closing');
      this.classList.add('cc-cart-opening');
      this.dataset.ccRevealState = 'opening';

      const result = originalOpen.apply(this, args);

      const timer = window.setTimeout(() => {
        this.classList.remove('cc-cart-opening');
        this.dataset.ccRevealState = 'open';
        silkTimers.delete(this);
      }, CC_SILK_OPEN_MS + 30);
      silkTimers.set(this, timer);
      return result;
    };

    if (typeof originalClose === 'function') {
      CartDrawer.prototype.close = function(...args) {
        if (!this.classList.contains('active') || this.dataset.ccRevealState === 'closing') return;

        clearSilkTimer(this);
        this.classList.remove('cc-cart-opening');
        this.classList.add('cc-cart-closing');
        this.dataset.ccRevealState = 'closing';

        if (reduceMotion && reduceMotion.matches) {
          const result = originalClose.apply(this, args);
          this.classList.remove('cc-cart-closing');
          this.dataset.ccRevealState = 'closed';
          return result;
        }

        /* Keep the drawer active while it visually leaves. This prevents the
           page/body state from being restored before the panel has finished. */
        const timer = window.setTimeout(() => {
          const result = originalClose.apply(this, args);
          this.classList.remove('cc-cart-closing');
          this.dataset.ccRevealState = 'closed';
          silkTimers.delete(this);
          return result;
        }, CC_SILK_CLOSE_MS);
        silkTimers.set(this, timer);
      };
    }

    CartDrawer.prototype.renderContents = function(data, skipOpen = false) {
      const suppressThisAdd = state.suppressAutoOpen;
      const wasActive = this.classList.contains('active');
      const bodyBefore = this.querySelector('.cart-drawer__body');
      const savedScrollTop = bodyBefore ? bodyBefore.scrollTop : 0;
      const beforeLayout = captureDrawerLayout(this);
      this.classList.add('cc-cart-section-sync');

      const result = originalRenderContents.call(
        this,
        data,
        suppressThisAdd ? true : skipOpen
      );

      /* The drawer was rebuilt from Shopify: settled lines and totals re-read
         from the new markup; lines still in motion keep their wanted quantity. */
      resetSettledLines();
      /* An open, empty drawer rebuilt in place keeps its jewellery box turning. */
      if (wasActive && this.classList.contains('is-empty')) requestAnimationFrame(() => animateEmptyState(this));

      if (wasActive) this.classList.add('active');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const bodyAfter = this.querySelector('.cart-drawer__body');
          if (bodyAfter) bodyAfter.scrollTop = savedScrollTop;
          animateDrawerReflow(beforeLayout, this);
          this.classList.remove('cc-cart-section-sync');
          /* no opacity pulse: keep the cart visually continuous */
        });
      });

      if (suppressThisAdd) {
        state.suppressAutoOpen = false;
        state.addStartedAt = -Infinity;
        window.clearTimeout(state.autoOpenSuppressionTimer);
      }

      return result;
    };
  });

  /* Watch Shopify's AJAX section replacement without interfering with it. */
  let mutationQueued = false;
  const observer = new MutationObserver((mutations) => {
    let cartChanged = false;

    for (const mutation of mutations) {
      const target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
      if (
        (target && target.closest && (
          target.closest('#CartDrawer-CartItems')
          || target.closest('#cart-icon-bubble')
        ))
        || Array.from(mutation.addedNodes).some((node) =>
          node.nodeType === 1
          && (
            node.id === 'CartDrawer-CartItems'
            || node.id === 'cart-icon-bubble'
            || (node.querySelector && (
              node.querySelector('#CartDrawer-CartItems')
              || node.querySelector('#cart-icon-bubble')
            ))
          )
        )
      ) {
        cartChanged = true;
        break;
      }
    }

    if (!cartChanged || mutationQueued) return;
    mutationQueued = true;

    requestAnimationFrame(() => {
      mutationQueued = false;
      const fresh = takeFreshRender();
      if (fresh !== null) receiveCount(fresh);
      if (state.lastAddButton) {
        flagArrivingItem();
        state.lastAddButton = null;
        window.clearTimeout(state.addIntentTimer);
      }
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  /* Drawer-open choreography + empty-state animation. */
  const drawerObserver = new MutationObserver(() => {
    const drawer = getDrawer();
    if (!drawer) return;

    const active = drawer.classList.contains('active');
    if (active && !state.drawerWasActive) {
      state.emptyAnimationRun = false;
      requestAnimationFrame(() => {
        if (drawer.classList.contains('is-empty')) {
          animateEmptyState(drawer);
          state.emptyAnimationRun = true;
        }
      });
    }

    if (!active) {
      state.emptyAnimationRun = false;
      stopEmptyState(drawer);
    }
    state.drawerWasActive = active;
  });

  const connectDrawerObserver = () => {
    const drawer = getDrawer();
    if (!drawer || drawer.dataset.ccDrawerObserved === 'true') return;
    drawer.dataset.ccDrawerObserved = 'true';
    drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    state.drawerWasActive = drawer.classList.contains('active');
  };

  connectDrawerObserver();

  const mountObserver = new MutationObserver(connectDrawerObserver);
  mountObserver.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', connectDrawerObserver);
})();
